import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Alert, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { FullScreenDocumentPreview } from "components/Document/shared";
import {
  DocumentUploadSection,
  type SubmissionDocument,
} from "components/Submission/DocumentUploadSection";
import { toSubmissionDocumentDisplay as toDisplayDocument } from "components/Submission/helpers/documentHelpers";
import { SwapSubmissionDocumentDialog } from "components/Submission/SwapSubmissionDocumentDialog";
import {
  GetSubmissionDocumentsStepQuery,
  SignatureStatus,
  SignatureType,
  SubmissionDocumentInput,
  SubmissionDocumentType,
  SubmissionStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useSubmissionIssuesGroupedByStep } from "hooks/useSubmissionSteps";
import { useTenant } from "hooks/useTenant";
import { useWasteStepper } from "hooks/useWasteStepper";
import { UPDATE_SUBMISSION_MUTATION } from "queries/submissions";
import { useCallback, useState } from "react";

import { GET_SUBMISSION_DOCUMENTS_STEP_QUERY, GET_SUBMISSION_ISSUES } from "../queries";
import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { DocumentStepStatusBanner } from "./DocumentStepStatusBanner";
import { StepContinueFooter } from "./StepContinueFooter";

type DocumentRow = NonNullable<
  NonNullable<GetSubmissionDocumentsStepQuery["submission"]>["documents"]
>[number];

function isAttachment(type: SubmissionDocumentType) {
  return type === SubmissionDocumentType.Attachment;
}

/** Map a server row back to the replace-semantics mutation input. */
function toDocumentInput(row: DocumentRow): SubmissionDocumentInput {
  return {
    documentId: row.document.id,
    type: row.type,
  };
}

/**
 * Document Preparation step (document forms): staff attach the filled-out
 * submission document plus any supporting attachments. Each add/delete
 * persists immediately through `updateSubmission`'s documents-only payload
 * (matching the Tier II report Documents step); the footer's Save only
 * touches the step. The payload is replace-semantics, so every save sends
 * both full arrays — signed/rejected rows always pass through untouched and
 * are never deletable here.
 */
export function PrepareDocumentStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const { canEdit, isFrozen, submission, refetchSubmission } = useWasteStepper();
  // Document editing is staff-only server-side (`updateSubmission` denies
  // document-form edits for non-staff); the extra isStaff keeps this gate
  // aligned with the server even if the shell's canEdit policy widens.
  const canEditDocuments = canEdit && isStaff;
  // Legacy keeps the staff-only swap/rename workaround available after filing,
  // even though adding or deleting document associations is frozen.
  const canSwapSubmissionDocument = isStaff;

  const { data, loading, refetch } = useQuery(GET_SUBMISSION_DOCUMENTS_STEP_QUERY, {
    variables: { id: submissionId },
    fetchPolicy: "cache-and-network",
  });
  const rows = data?.submission?.documents ?? [];
  const submissionRows = rows.filter((row) => !isAttachment(row.type));
  const attachmentRows = rows.filter((row) => isAttachment(row.type));
  // Issues come from the same grouped hook the stepper shell uses (backed by
  // the already-running GET_SUBMISSION_ISSUES query), so the step doesn't run
  // a second submission validation of its own.
  const issuesByStep = useSubmissionIssuesGroupedByStep(facilityId, [], submissionId);
  const documentIssues = issuesByStep[SubmissionStepType.PrepareDocument] ?? [];
  const hasRejectedDocument = submissionRows.some(
    (row) => row.type === SubmissionDocumentType.RejectedSubmission
  );

  const [updateSubmission, { loading: saving }] = useMutation(UPDATE_SUBMISSION_MUTATION, {
    // The sidebar issue badge comes from a separate compliance query. Refresh
    // both views so adding the required document clears the Prepare Document
    // issue immediately without a page reload.
    refetchQueries: [GET_SUBMISSION_DOCUMENTS_STEP_QUERY, GET_SUBMISSION_ISSUES],
    awaitRefetchQueries: true,
  });

  // Replace-semantics save: always send BOTH full arrays, with the one change
  // applied — dropping a row here deletes its association server-side.
  const persistDocuments = useCallback(
    async (
      submissionDocuments: SubmissionDocumentInput[],
      attachmentDocuments: SubmissionDocumentInput[]
    ) => {
      try {
        await updateSubmission({
          variables: { input: { id: submissionId, submissionDocuments, attachmentDocuments } },
        });
      } catch (error) {
        alerts.error("Error saving documents.", error);
      }
    },
    [updateSubmission, submissionId, alerts]
  );

  const [dialogState, setDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [addingType, setAddingType] = useState<SubmissionDocumentType>(
    SubmissionDocumentType.Submission
  );

  const handleAdd = (type: SubmissionDocumentType) => {
    setAddingType(type);
    setDialogState({ ...defaultAddEditDocumentDialogState, open: true });
  };

  // The dialog uploads/creates the Document; associating it with the
  // submission is this immediate save.
  const handleDocumentSubmit = async (document: { id: string }) => {
    const added: SubmissionDocumentInput = {
      documentId: document.id,
      type: addingType,
    };
    await persistDocuments(
      [...submissionRows.map(toDocumentInput), ...(isAttachment(addingType) ? [] : [added])],
      [...attachmentRows.map(toDocumentInput), ...(isAttachment(addingType) ? [added] : [])]
    );
  };

  const handleDelete = async (documentId: string) => {
    await persistDocuments(
      submissionRows.filter((row) => row.document.id !== documentId).map(toDocumentInput),
      attachmentRows.filter((row) => row.document.id !== documentId).map(toDocumentInput)
    );
  };

  // Row-name/eyeball click: open the stored file in a new tab via a
  // short-lived inline link (attachment disposition would download it).
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const [previewDocument, setPreviewDocument] = useState<SubmissionDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const handlePreviewDocument = async (doc: SubmissionDocument) => {
    try {
      const { data: linkData } = await getDocumentDownloadLink({
        variables: { id: doc.id, inline: true },
      });
      if (!linkData?.getDocumentDownloadLink) {
        throw new Error("No download link returned");
      }
      setPreviewDocument(doc);
      setPreviewUrl(linkData.getDocumentDownloadLink);
      setPreviewOpen(true);
    } catch (error) {
      alerts.error("Error previewing document.", error);
    }
  };

  // Signed/rejected rows are preserved by every save and surface read-only;
  // deleting one would sabotage the signing trail.
  const canDeleteSubmissionRow = (doc: SubmissionDocument) =>
    canEditDocuments && doc.submissionDocumentType === SubmissionDocumentType.Submission;
  const canDeleteAttachmentRow = () => canEditDocuments;

  const [swapTarget, setSwapTarget] = useState<SubmissionDocument | null>(null);
  // Swapping a prepared or already-signed digital document resets the signature;
  // suppressed post-completion and for non-digital types (nothing to invalidate).
  const swapWillInvalidateSignature = (() => {
    if (
      submission?.status === SubmissionStatus.FilingComplete ||
      submission?.signatureType !== SignatureType.Digital
    ) {
      return false;
    }
    const targetExternalDocumentId = rows.find(
      (row) => row.id === swapTarget?.submissionDocumentId
    )?.externalDocumentId;
    const anySignerSigned = (submission?.reviewers ?? []).some(
      (r) =>
        r.isSigner &&
        (r.signatureStatus === SignatureStatus.Sent ||
          r.signatureStatus === SignatureStatus.Completed)
    );
    return Boolean(targetExternalDocumentId) || anySignerSigned;
  })();

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Document Preparation"
        // Read-only viewers can't attach, so describe rather than instruct.
        description={
          canEditDocuments
            ? "Attach the completed submission document and any supporting attachments."
            : "Encamp staff prepare the submission document and any supporting attachments."
        }
      />
      <DocumentStepStatusBanner step={SubmissionStepType.PrepareDocument} />
      <Stack spacing={1}>
        <StepSubHeader
          title="Submission document"
          action={
            canEditDocuments && (
              <Button
                startIcon={<Add />}
                disabled={saving}
                onClick={() => handleAdd(SubmissionDocumentType.Submission)}
              >
                Add Document
              </Button>
            )
          }
        />
        {submissionRows.length > 0 && (
          <DocumentUploadSection
            documents={submissionRows.map(toDisplayDocument)}
            onDeleteDocument={handleDelete}
            onSelectDocument={handlePreviewDocument}
            hideSelectIcon
            onOpenFullScreen={handlePreviewDocument}
            canDeleteDocument={canDeleteSubmissionRow}
            canSwapDocument={canSwapSubmissionDocument}
            onSwapDocument={setSwapTarget}
            tenantId={tenantId ?? undefined}
          />
        )}
        {loading && submissionRows.length === 0 && <Skeleton variant="rounded" height={48} />}
        {!loading && submissionRows.length === 0 && isFrozen && (
          <Typography variant="body2" color="text.secondary">
            No submission document has been added.
          </Typography>
        )}
        {/* One gate predicate for the whole panel: editors (canEditDocuments)
            see the actionable error, everyone else the informational alert. */}
        {!loading && !isFrozen && canEditDocuments && documentIssues.length > 0 && (
          <Alert severity="error">
            {documentIssues.map((issue) => (
              <div key={`${issue.key}-${issue.message}`}>{issue.message}</div>
            ))}
          </Alert>
        )}
        {!loading && !isFrozen && !canEditDocuments && hasRejectedDocument && (
          <Alert severity="info">
            This submission document was rejected. Encamp staff are addressing the feedback.
          </Alert>
        )}
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader
          title={canEditDocuments ? "Additional attachments (optional)" : "Additional attachments"}
          action={
            canEditDocuments && (
              <Button
                startIcon={<Add />}
                disabled={saving}
                onClick={() => handleAdd(SubmissionDocumentType.Attachment)}
              >
                Add Attachment
              </Button>
            )
          }
        />
        {attachmentRows.length > 0 ? (
          <DocumentUploadSection
            documents={attachmentRows.map(toDisplayDocument)}
            onDeleteDocument={handleDelete}
            onSelectDocument={handlePreviewDocument}
            hideSelectIcon
            onOpenFullScreen={handlePreviewDocument}
            canDeleteDocument={canDeleteAttachmentRow}
            tenantId={tenantId ?? undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No attachments have been added.
          </Typography>
        )}
      </Stack>
      <StepContinueFooter
        submissionId={submissionId}
        step={SubmissionStepType.PrepareDocument}
        backStep={SubmissionStepType.Understand}
      />
      {dialogState.open && (
        <AddEditDocumentDialog
          dialogState={dialogState}
          setDialogState={setDialogState}
          tenantId={tenantId || ""}
          showFacilityPicker={false}
          refetchQueries={[]}
          onSubmit={handleDocumentSubmit}
        />
      )}
      {swapTarget?.submissionDocumentId && tenantId && (
        <SwapSubmissionDocumentDialog
          open
          onClose={() => setSwapTarget(null)}
          submissionId={submissionId}
          submissionDocumentId={swapTarget.submissionDocumentId}
          tenantId={tenantId}
          currentDocumentName={swapTarget.name}
          currentDocumentType={swapTarget.documentType}
          signatureWillBeInvalidated={swapWillInvalidateSignature}
          onSuccess={async () => {
            await Promise.all([refetch(), refetchSubmission()]);
          }}
        />
      )}
      {previewDocument && previewUrl && (
        <FullScreenDocumentPreview
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          fileUrl={previewUrl}
          fileExtension={previewDocument.fileExtension ?? undefined}
          title={previewDocument.name}
        />
      )}
    </Stack>
  );
}
